import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PlantMachineryForm } from '../components/forms/PlantMachineryForm';
import { ServiceRecordForm } from '../components/forms/ServiceRecordForm';
import { formatDisplayDate, getCurrentAESTDate } from '../utils/dateUtils';
import { Plus, Wrench, Truck, Settings, Calendar, AlertTriangle, CheckCircle, Clock, CreditCard as Edit, Trash2, Eye, Search, Filter, TrendingUp, DollarSign, X, Paperclip, MapPin, Save, Package } from 'lucide-react';

interface Vehicle {
  id: string;
  registration: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  status: string;
  current_location?: string;
  current_km?: number;
  current_hours?: number;
  service_interval_km?: number;
  service_interval_hours?: number;
  service_interval_months?: number;
  last_service_date?: string;
  last_service_km?: number;
  last_service_hours?: number;
  next_service_due_date?: string;
  next_service_due_km?: number;
  next_service_due_hours?: number;
  created_at: string;
}

interface PlantMachinery {
  id: string;
  asset_number: string;
  name: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  serial_number?: string;
  pin_size?: string;
  between_ears?: string;
  pin_centres?: string;
  status: string;
  current_location?: string;
  service_interval_hours?: number;
  service_interval_months?: number;
  current_hours?: number;
  last_service_date?: string;
  last_service_hours?: number;
  next_service_due_date?: string;
  next_service_due_hours?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface EquipmentAttachment {
  id: string;
  equipment_id: string;
  equipment_type: string;
  name: string;
  location: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface ServiceRecord {
  id: string;
  asset_id: string;
  asset_type: 'vehicle' | 'plant';
  service_date: string;
  service_type: string;
  hours_at_service?: number;
  km_at_service?: number;
  next_due_date?: string;
  next_due_hours?: number;
  next_due_km?: number;
  service_provider?: string;
  cost?: number;
  description: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface AssetPart {
  id: string;
  asset_id: string;
  asset_type: 'vehicle' | 'plant';
  part_category: string;
  part_name: string;
  part_number: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface AssetWithServices {
  id: string;
  name: string;
  type: string;
  asset_type: 'vehicle' | 'plant';
  status: string;
  current_location?: string;
  last_service_date?: string;
  next_service_due_date?: string;
  days_until_service?: number;
  service_overdue?: boolean;
  service_records?: ServiceRecord[];
  attachments?: EquipmentAttachment[];
  parts?: AssetPart[];
  // Vehicle specific
  registration?: string;
  current_km?: number;
  next_service_due_km?: number;
  service_interval_km?: number;
  // Plant specific
  asset_number?: string;
  // Common detail fields
  make?: string;
  model?: string;
  year?: number;
  serial_number?: string;
  pin_size?: string;
  between_ears?: string;
  pin_centres?: string;
  // Both vehicles and plant
  current_hours?: number;
  next_service_due_hours?: number;
  service_interval_hours?: number;
  service_interval_months?: number;
}

export const VehiclePlantServicingPage: React.FC = () => {
  const [assets, setAssets] = useState<AssetWithServices[]>([]);
  const [rawPlantData, setRawPlantData] = useState<PlantMachinery[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingPlant, setEditingPlant] = useState<PlantMachinery | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetWithServices | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newAttachment, setNewAttachment] = useState({ name: '', location: '', notes: '' });
  const [editingAttachment, setEditingAttachment] = useState<EquipmentAttachment | null>(null);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [editingLocationValue, setEditingLocationValue] = useState('');
  const [newPart, setNewPart] = useState({ part_category: 'oil_filter', part_name: '', part_number: '', notes: '' });
  const [editingPart, setEditingPart] = useState<AssetPart | null>(null);

  useEffect(() => {
    fetchAssetsWithServices();
  }, []);

  const fetchAssetsWithServices = async () => {
    try {
      // Fetch vehicles (exclude trailers - they don't need servicing tracking)
      const { data: vehiclesData, error: vehiclesError } = await supabase
        .from('vehicles')
        .select('*')
        .neq('type', 'trailer')
        .order('registration');

      if (vehiclesError) throw vehiclesError;

      // Fetch plant machinery
      const { data: plantData, error: plantError } = await supabase
        .from('plant_machinery')
        .select('*')
        .order('asset_number');

      if (plantError) throw plantError;

      // Fetch service records
      const { data: serviceRecords, error: serviceError } = await supabase
        .from('service_records')
        .select('*')
        .order('service_date', { ascending: false });

      if (serviceError) throw serviceError;

      // Fetch attachments
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('equipment_attachments')
        .select('*')
        .order('name');

      if (attachmentsError) throw attachmentsError;

      // Fetch asset parts
      const { data: partsData, error: partsError } = await supabase
        .from('asset_parts')
        .select('*')
        .order('part_category');

      if (partsError) throw partsError;

      // Fetch latest completed pre-start check per equipment for current readings
      const { data: prestartData, error: prestartError } = await supabase
        .from('prestart_checks')
        .select('equipment_id, equipment_type, reading_value, reading_unit, created_at')
        .eq('status', 'complete')
        .order('created_at', { ascending: false });

      if (prestartError) throw prestartError;

      // Build a map of equipment_id -> latest reading (first occurrence is most recent due to order)
      const latestReadings = new Map<string, { value: number; unit: string }>();
      (prestartData || []).forEach(check => {
        if (!latestReadings.has(check.equipment_id) && check.reading_value != null) {
          latestReadings.set(check.equipment_id, {
            value: Number(check.reading_value),
            unit: check.reading_unit
          });
        }
      });

      // Combine and process assets
      const today = getCurrentAESTDate();
      const combinedAssets: AssetWithServices[] = [];

      // Process vehicles
      (vehiclesData || []).forEach(vehicle => {
        const vehicleServices = (serviceRecords || []).filter(
          record => record.asset_id === vehicle.id && record.asset_type === 'vehicle'
        );
        const vehicleAttachments = (attachmentsData || []).filter(
          attachment => attachment.equipment_id === vehicle.id && attachment.equipment_type === 'vehicle'
        );
        const vehicleParts = (partsData || []).filter(
          part => part.asset_id === vehicle.id && part.asset_type === 'vehicle'
        );

        // Use latest pre-start reading if available for current km/hours
        const latestReading = latestReadings.get(vehicle.id);
        const currentKm = latestReading?.unit === 'km'
          ? Math.max(latestReading.value, vehicle.current_km || 0)
          : vehicle.current_km;
        const currentHours = latestReading?.unit === 'hours'
          ? Math.max(latestReading.value, vehicle.current_hours || 0)
          : vehicle.current_hours;

        const lastService = vehicleServices.length > 0 ? vehicleServices[0] : null;
        let daysUntilService = 0;
        let serviceOverdue = false;

        // Check date-based service schedule
        if (vehicle.next_service_due_date) {
          const nextDueDate = new Date(vehicle.next_service_due_date);
          const diffTime = nextDueDate.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else if (lastService?.next_due_date) {
          const nextDueDate = new Date(lastService.next_due_date);
          const diffTime = nextDueDate.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else if (vehicle.service_interval_months && vehicle.last_service_date) {
          const lastServiceDate = new Date(vehicle.last_service_date);
          const nextDueDate = new Date(lastServiceDate);
          nextDueDate.setMonth(lastServiceDate.getMonth() + vehicle.service_interval_months);
          const diffTime = nextDueDate.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else if (lastService?.service_date && !lastService?.next_due_date) {
          // If no next due date, assume 6 months from last service
          const lastServiceDate = new Date(lastService.service_date);
          const estimatedNextDue = new Date(lastServiceDate);
          estimatedNextDue.setMonth(lastServiceDate.getMonth() + 6);
          const diffTime = estimatedNextDue.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else {
          // No service records - consider overdue only if no next due date is set
          serviceOverdue = !vehicle.next_service_due_date && !lastService;
        }

        // Check kilometer-based service if applicable
        const kmOverdue = vehicle.service_interval_km && currentKm && vehicle.next_service_due_km
          ? currentKm >= vehicle.next_service_due_km
          : false;

        // Check hour-based service if applicable
        const hoursOverdue = vehicle.service_interval_hours && currentHours && vehicle.next_service_due_hours
          ? currentHours >= vehicle.next_service_due_hours
          : false;

        combinedAssets.push({
          id: vehicle.id,
          name: `${vehicle.registration}${vehicle.make && vehicle.model ? ` (${vehicle.make} ${vehicle.model})` : ''}`,
          type: vehicle.type,
          asset_type: 'vehicle',
          status: vehicle.status,
          current_location: vehicle.current_location,
          registration: vehicle.registration,
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          current_km: currentKm,
          current_hours: currentHours,
          next_service_due_km: vehicle.next_service_due_km,
          next_service_due_hours: vehicle.next_service_due_hours,
          service_interval_km: vehicle.service_interval_km,
          service_interval_hours: vehicle.service_interval_hours,
          service_interval_months: vehicle.service_interval_months,
          last_service_date: vehicle.last_service_date || lastService?.service_date,
          next_service_due_date: vehicle.next_service_due_date || lastService?.next_due_date,
          days_until_service: daysUntilService,
          service_overdue: serviceOverdue || kmOverdue || hoursOverdue,
          service_records: vehicleServices,
          attachments: vehicleAttachments,
          parts: vehicleParts
        });
      });

      setRawPlantData(plantData || []);

      // Process plant machinery
      (plantData || []).forEach(plant => {
        const plantServices = (serviceRecords || []).filter(
          record => record.asset_id === plant.id && record.asset_type === 'plant'
        );
        const plantAttachments = (attachmentsData || []).filter(
          attachment => attachment.equipment_id === plant.id && attachment.equipment_type === 'plant'
        );
        const plantParts = (partsData || []).filter(
          part => part.asset_id === plant.id && part.asset_type === 'plant'
        );

        // Use latest pre-start reading if available for current hours
        const latestReading = latestReadings.get(plant.id);
        const currentHours = latestReading?.unit === 'hours'
          ? Math.max(latestReading.value, plant.current_hours || 0)
          : plant.current_hours;

        const lastService = plantServices.length > 0 ? plantServices[0] : null;
        let daysUntilService = 0;
        let serviceOverdue = false;

        // Check both date-based and hour-based service schedules
        if (plant.next_service_due_date) {
          const nextDueDate = new Date(plant.next_service_due_date);
          const diffTime = nextDueDate.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else if (plant.service_interval_months && plant.last_service_date) {
          const lastServiceDate = new Date(plant.last_service_date);
          const nextDueDate = new Date(lastServiceDate);
          nextDueDate.setMonth(lastServiceDate.getMonth() + plant.service_interval_months);
          const diffTime = nextDueDate.getTime() - today.getTime();
          daysUntilService = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          serviceOverdue = daysUntilService < 0;
        } else {
          // No service schedule set - consider overdue if no recent service
          serviceOverdue = !plant.last_service_date;
        }

        // Also check hour-based service if applicable
        const hoursOverdue = plant.service_interval_hours && currentHours && plant.next_service_due_hours
          ? currentHours >= plant.next_service_due_hours
          : false;

        combinedAssets.push({
          id: plant.id,
          name: plant.name,
          type: plant.type,
          asset_type: 'plant',
          status: plant.status,
          current_location: plant.current_location,
          asset_number: plant.asset_number,
          make: plant.make,
          model: plant.model,
          year: plant.year,
          serial_number: plant.serial_number,
          pin_size: plant.pin_size,
          between_ears: plant.between_ears,
          pin_centres: plant.pin_centres,
          current_hours: currentHours,
          next_service_due_hours: plant.next_service_due_hours,
          service_interval_hours: plant.service_interval_hours,
          service_interval_months: plant.service_interval_months,
          last_service_date: plant.last_service_date,
          next_service_due_date: plant.next_service_due_date,
          days_until_service: daysUntilService,
          service_overdue: serviceOverdue || hoursOverdue,
          service_records: plantServices,
          attachments: plantAttachments,
          parts: plantParts
        });
      });

      setAssets(combinedAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPlant = async (plantData: Partial<PlantMachinery>) => {
    setSubmitting(true);
    try {
      if (editingPlant) {
        const { error } = await supabase
          .from('plant_machinery')
          .update(plantData)
          .eq('id', editingPlant.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('plant_machinery')
          .insert([plantData]);
        
        if (error) throw error;
      }
      
      setShowPlantModal(false);
      setEditingPlant(null);
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error saving plant machinery:', error);
      alert('Failed to save plant machinery. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitService = async (serviceData: Partial<ServiceRecord>) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('service_records')
        .insert([serviceData]);

      if (error) throw error;

      // Only update the asset's service schedule for routine or major services
      const isScheduledService = serviceData.service_type === 'routine' || serviceData.service_type === 'major';

      if (selectedAsset && isScheduledService) {
        const updateData: any = {
          last_service_date: serviceData.service_date,
          next_service_due_date: serviceData.next_due_date
        };

        if (selectedAsset.asset_type === 'plant') {
          if (serviceData.hours_at_service) {
            updateData.last_service_hours = serviceData.hours_at_service;
          }

          if (serviceData.next_due_hours) {
            updateData.next_service_due_hours = serviceData.next_due_hours;
          }

          await supabase
            .from('plant_machinery')
            .update(updateData)
            .eq('id', selectedAsset.id);
        } else if (selectedAsset.asset_type === 'vehicle') {
          if (serviceData.km_at_service) {
            updateData.last_service_km = serviceData.km_at_service;
            updateData.current_km = serviceData.km_at_service;
          }

          if (serviceData.next_due_km) {
            updateData.next_service_due_km = serviceData.next_due_km;
          }

          if (serviceData.hours_at_service) {
            updateData.last_service_hours = serviceData.hours_at_service;
            updateData.current_hours = serviceData.hours_at_service;
          }

          if (serviceData.next_due_hours) {
            updateData.next_service_due_hours = serviceData.next_due_hours;
          }

          await supabase
            .from('vehicles')
            .update(updateData)
            .eq('id', selectedAsset.id);
        }
      }

      setShowServiceModal(false);
      setSelectedAsset(null);
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error saving service record:', error);
      alert('Failed to save service record. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteService = async (serviceId: string, assetId: string, assetType: 'vehicle' | 'plant') => {
    if (!confirm('Are you sure you want to delete this service record? The service schedule will be recalculated from the remaining records.')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('service_records')
        .delete()
        .eq('id', serviceId);

      if (deleteError) throw deleteError;

      const { data: remainingServices, error: fetchError } = await supabase
        .from('service_records')
        .select('*')
        .eq('asset_id', assetId)
        .eq('asset_type', assetType)
        .order('service_date', { ascending: false });

      if (fetchError) throw fetchError;

      const scheduledServices = (remainingServices || []).filter(
        (s: ServiceRecord) => s.service_type === 'routine' || s.service_type === 'major'
      );
      const lastScheduledService = scheduledServices.length > 0 ? scheduledServices[0] : null;

      const updateData: Record<string, any> = {
        last_service_date: lastScheduledService?.service_date || null,
        next_service_due_date: lastScheduledService?.next_due_date || null,
      };

      if (assetType === 'plant') {
        updateData.last_service_hours = lastScheduledService?.hours_at_service || null;
        updateData.next_service_due_hours = lastScheduledService?.next_due_hours || null;
      } else {
        updateData.last_service_km = lastScheduledService?.km_at_service || null;
        updateData.next_service_due_km = lastScheduledService?.next_due_km || null;
        updateData.last_service_hours = lastScheduledService?.hours_at_service || null;
        updateData.next_service_due_hours = lastScheduledService?.next_due_hours || null;
        if (lastScheduledService) {
          updateData.current_km = lastScheduledService.km_at_service || null;
          updateData.current_hours = lastScheduledService.hours_at_service || null;
        }
      }

      const table = assetType === 'vehicle' ? 'vehicles' : 'plant_machinery';
      const { error: updateError } = await supabase
        .from(table)
        .update(updateData)
        .eq('id', assetId);

      if (updateError) throw updateError;

      if (selectedAsset && selectedAsset.id === assetId) {
        setSelectedAsset({
          ...selectedAsset,
          service_records: remainingServices || [],
          last_service_date: lastScheduledService?.service_date || undefined,
          next_service_due_date: lastScheduledService?.next_due_date || undefined,
        });
      }

      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error deleting service record:', error);
      alert('Failed to delete service record. Please try again.');
    }
  };

  const handleDeletePlant = async (plantId: string) => {
    if (confirm('Are you sure you want to delete this plant/machinery? This will also delete all associated service records.')) {
      try {
        const { error } = await supabase
          .from('plant_machinery')
          .delete()
          .eq('id', plantId);

        if (error) throw error;
        fetchAssetsWithServices();
      } catch (error) {
        console.error('Error deleting plant machinery:', error);
        alert('Failed to delete plant machinery. Please try again.');
      }
    }
  };

  const handleAddAttachment = async () => {
    if (!selectedAsset || !newAttachment.name.trim()) {
      alert('Please enter an attachment name');
      return;
    }

    try {
      const { error } = await supabase
        .from('equipment_attachments')
        .insert([{
          equipment_id: selectedAsset.id,
          equipment_type: selectedAsset.asset_type,
          name: newAttachment.name,
          location: newAttachment.location,
          notes: newAttachment.notes
        }]);

      if (error) throw error;

      setNewAttachment({ name: '', location: '', notes: '' });
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error adding attachment:', error);
      alert('Failed to add attachment. Please try again.');
    }
  };

  const handleUpdateAttachment = async (attachment: EquipmentAttachment) => {
    try {
      const { error } = await supabase
        .from('equipment_attachments')
        .update({
          name: attachment.name,
          location: attachment.location,
          notes: attachment.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', attachment.id);

      if (error) throw error;

      setEditingAttachment(null);
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error updating attachment:', error);
      alert('Failed to update attachment. Please try again.');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (confirm('Are you sure you want to delete this attachment?')) {
      try {
        const { error } = await supabase
          .from('equipment_attachments')
          .delete()
          .eq('id', attachmentId);

        if (error) throw error;
        fetchAssetsWithServices();
      } catch (error) {
        console.error('Error deleting attachment:', error);
        alert('Failed to delete attachment. Please try again.');
      }
    }
  };

  const handleAddPart = async () => {
    if (!selectedAsset || !newPart.part_name.trim() || !newPart.part_number.trim()) {
      alert('Please enter both a part name and part number');
      return;
    }

    try {
      const { error } = await supabase
        .from('asset_parts')
        .insert([{
          asset_id: selectedAsset.id,
          asset_type: selectedAsset.asset_type,
          part_category: newPart.part_category,
          part_name: newPart.part_name,
          part_number: newPart.part_number,
          notes: newPart.notes || null
        }]);

      if (error) throw error;

      setNewPart({ part_category: 'oil_filter', part_name: '', part_number: '', notes: '' });
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error adding part:', error);
      alert('Failed to add part. Please try again.');
    }
  };

  const handleUpdatePart = async (part: AssetPart) => {
    try {
      const { error } = await supabase
        .from('asset_parts')
        .update({
          part_category: part.part_category,
          part_name: part.part_name,
          part_number: part.part_number,
          notes: part.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', part.id);

      if (error) throw error;

      setEditingPart(null);
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error updating part:', error);
      alert('Failed to update part. Please try again.');
    }
  };

  const handleDeletePart = async (partId: string) => {
    if (confirm('Are you sure you want to delete this part?')) {
      try {
        const { error } = await supabase
          .from('asset_parts')
          .delete()
          .eq('id', partId);

        if (error) throw error;
        fetchAssetsWithServices();
      } catch (error) {
        console.error('Error deleting part:', error);
        alert('Failed to delete part. Please try again.');
      }
    }
  };

  const PART_CATEGORIES = [
    { value: 'oil_filter', label: 'Oil Filter' },
    { value: 'fuel_filter', label: 'Fuel Filter' },
    { value: 'hydraulic_filter', label: 'Hydraulic Filter' },
    { value: 'air_filter', label: 'Air Filter' },
    { value: 'other', label: 'Other Spare Part' }
  ];

  const getPartCategoryLabel = (value: string) => PART_CATEGORIES.find(c => c.value === value)?.label || value;

  const getPartCategoryColor = (value: string) => {
    switch (value) {
      case 'oil_filter': return 'bg-amber-100 text-amber-800';
      case 'fuel_filter': return 'bg-orange-100 text-orange-800';
      case 'hydraulic_filter': return 'bg-blue-100 text-blue-800';
      case 'air_filter': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleUpdateLocation = async (assetId: string, assetType: 'vehicle' | 'plant', location: string) => {
    try {
      const table = assetType === 'vehicle' ? 'vehicles' : 'plant_machinery';
      const { error } = await supabase
        .from(table)
        .update({ current_location: location })
        .eq('id', assetId);

      if (error) throw error;

      setEditingLocationId(null);
      setEditingLocationValue('');
      fetchAssetsWithServices();
    } catch (error) {
      console.error('Error updating location:', error);
      alert('Failed to update location. Please try again.');
    }
  };

  const getServiceStatusColor = (asset: AssetWithServices) => {
    if (asset.service_overdue) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (asset.days_until_service !== undefined && asset.days_until_service <= 30) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    if (asset.days_until_service !== undefined && asset.days_until_service <= 90) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusIcon = (isOverdue: boolean, daysUntilService?: number) => {
    if (isOverdue) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
    
    if (daysUntilService !== undefined && daysUntilService <= 30) {
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    }
    
    if (daysUntilService !== undefined && daysUntilService <= 90) {
      return <Clock className="h-4 w-4 text-blue-600" />;
    }
    
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const getStatusText = (isOverdue: boolean, daysUntilService?: number, hasRecord?: boolean) => {
    if (!hasRecord) {
      return 'No Service Record';
    }
    
    if (isOverdue) {
      return `Overdue (${Math.abs(daysUntilService || 0)} days)`;
    }
    
    if (daysUntilService !== undefined && daysUntilService <= 30) {
      return `Due Soon (${daysUntilService} days)`;
    }
    
    if (daysUntilService !== undefined && daysUntilService <= 90) {
      return `Due in ${daysUntilService} days`;
    }
    
    return `Current (${daysUntilService} days)`;
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.asset_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.registration?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || asset.asset_type === filterType;
    const matchesStatus = !filterStatus || asset.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const vehicleAssets = assets.filter(a => a.asset_type === 'vehicle');
  const plantAssets = assets.filter(a => a.asset_type === 'plant');
  const overdueCount = assets.filter(a => a.service_overdue).length;
  const dueSoonCount = assets.filter(a => !a.service_overdue && (a.days_until_service || 0) <= 30 && (a.days_until_service || 0) >= 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle & Plant Servicing</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track service schedules for vehicles, plant, and machinery
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPlant(null);
            setShowPlantModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Plant/Machinery</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Vehicles</p>
              <p className="text-xl font-bold text-gray-900">{vehicleAssets.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Settings className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Plant & Machinery</p>
              <p className="text-xl font-bold text-gray-900">{plantAssets.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Service Overdue</p>
              <p className="text-xl font-bold text-red-600">{overdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Due Soon</p>
              <p className="text-xl font-bold text-yellow-600">{dueSoonCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Wrench className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Assets</p>
              <p className="text-xl font-bold text-gray-900">{assets.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert for overdue services */}
      {overdueCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-medium text-red-900">
                Service Maintenance Required
              </h3>
              <p className="text-sm text-red-700">
                {overdueCount} assets have overdue services • {dueSoonCount} assets due for service within 30 days
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, registration, or asset number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Types</option>
              <option value="vehicle">Vehicles</option>
              <option value="plant">Plant & Machinery</option>
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
              <option value="sold">Sold</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAssets.map((asset) => (
          <div
            key={`${asset.asset_type}-${asset.id}`}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            {/* Asset Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  asset.asset_type === 'vehicle' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {asset.asset_type === 'vehicle' ? (
                    <Truck className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Settings className="h-5 w-5 text-purple-600" />
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-medium text-gray-900">{asset.name}</h3>
                    {asset.asset_type === 'plant' && (asset.type === 'excavator' || asset.type === 'skid_steer') && asset.attachments && asset.attachments.length > 0 && (
                      <div className="flex items-center space-x-1 px-2 py-0.5 bg-orange-100 rounded-full" title={`${asset.attachments.length} attachment${asset.attachments.length > 1 ? 's' : ''}`}>
                        <Paperclip className="h-3 w-3 text-orange-600" />
                        <span className="text-xs font-medium text-orange-600">{asset.attachments.length}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 capitalize">{asset.type.replace('_', ' ')}</p>
                  {asset.asset_number && (
                    <p className="text-xs text-gray-500 font-mono">{asset.asset_number}</p>
                  )}
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setSelectedAsset(asset);
                    setShowDetailModal(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="View Details"
                >
                  <Eye className="h-4 w-4" />
                </button>
                {asset.asset_type === 'plant' && (
                  <>
                    <button
                      onClick={() => {
                        const plant = rawPlantData.find(p => p.id === asset.id);
                        if (plant) {
                          setEditingPlant(plant);
                          setShowPlantModal(true);
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Edit Plant"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePlant(asset.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete Plant"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Current Location - Inline Editable */}
            <div className="mb-4 pb-3 border-b border-gray-200">
              {editingLocationId === asset.id ? (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <input
                    type="text"
                    value={editingLocationValue}
                    onChange={(e) => setEditingLocationValue(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter location (e.g., Site A, Workshop)"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUpdateLocation(asset.id, asset.asset_type, editingLocationValue);
                      } else if (e.key === 'Escape') {
                        setEditingLocationId(null);
                        setEditingLocationValue('');
                      }
                    }}
                  />
                  <button
                    onClick={() => handleUpdateLocation(asset.id, asset.asset_type, editingLocationValue)}
                    className="p-1 text-green-600 hover:text-green-800 transition-colors"
                    title="Save"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingLocationId(null);
                      setEditingLocationValue('');
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingLocationId(asset.id);
                    setEditingLocationValue(asset.current_location || '');
                  }}
                  className="flex items-center space-x-2 cursor-pointer group hover:bg-gray-50 rounded px-2 py-1 -mx-2"
                  title="Click to edit location"
                >
                  <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700 flex-1">
                    {asset.current_location || 'No location set'}
                  </span>
                  <Edit className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>

            {/* Service Status */}
            <div className={`rounded-lg p-3 border mb-4 ${getServiceStatusColor(asset)}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Wrench className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {asset.service_overdue ? 'Service Overdue' :
                     (asset.days_until_service !== undefined && asset.days_until_service <= 30) ? 'Service Due' :
                     'Service Status'}
                  </span>
                </div>
                {getStatusIcon(asset.service_overdue || false, asset.days_until_service)}
              </div>
              
              <div className="space-y-1">
                <div>
                  <p className="text-xs font-medium">Last Service:</p>
                  <p className="text-sm font-bold">
                    {asset.last_service_date 
                      ? formatDisplayDate(asset.last_service_date)
                      : 'No Record'
                    }
                  </p>
                </div>
                
                <div>
                  <p className="text-xs font-medium">Next Due:</p>
                  <p className="text-sm font-bold">
                    {asset.next_service_due_date 
                      ? formatDisplayDate(asset.next_service_due_date)
                      : 'Not Scheduled'
                    }
                  </p>
                </div>
                
                <div className="pt-1">
                  <span className="text-xs font-medium">
                    {getStatusText(
                      asset.service_overdue || false, 
                      asset.days_until_service,
                      !!asset.last_service_date
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Vehicle-specific info */}
            {asset.asset_type === 'vehicle' && (asset.current_km !== undefined || asset.next_service_due_km !== undefined || asset.current_hours !== undefined || asset.next_service_due_hours !== undefined) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="space-y-2 text-sm">
                  {asset.current_km !== undefined && asset.current_km !== null && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Current Kilometers:</span>
                      <span className="font-medium text-blue-900">{Math.round(asset.current_km).toLocaleString()} km</span>
                    </div>
                  )}
                  {asset.next_service_due_km !== undefined && asset.next_service_due_km !== null && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Service Due At:</span>
                      <span className="font-medium text-blue-900">{Math.round(asset.next_service_due_km).toLocaleString()} km</span>
                    </div>
                  )}
                  {asset.service_interval_km && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">KM Interval:</span>
                      <span className="font-medium text-blue-900">{asset.service_interval_km.toLocaleString()} km</span>
                    </div>
                  )}
                  {asset.current_hours !== undefined && asset.current_hours !== null && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Current Hours:</span>
                      <span className="font-medium text-blue-900">{asset.current_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                    </div>
                  )}
                  {asset.next_service_due_hours !== undefined && asset.next_service_due_hours !== null && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Service Due Hours:</span>
                      <span className="font-medium text-blue-900">{asset.next_service_due_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                    </div>
                  )}
                  {asset.service_interval_hours && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Hours Interval:</span>
                      <span className="font-medium text-blue-900">{asset.service_interval_hours}h</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Plant-specific info */}
            {asset.asset_type === 'plant' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                <div className="space-y-2 text-sm">
                  {asset.current_hours !== undefined && asset.current_hours !== null && (
                    <div className="flex justify-between">
                      <span className="text-purple-700">Current Hours:</span>
                      <span className="font-medium text-purple-900">{asset.current_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                    </div>
                  )}
                  {asset.next_service_due_hours !== undefined && asset.next_service_due_hours !== null && (
                    <div className="flex justify-between">
                      <span className="text-purple-700">Service Due Hours:</span>
                      <span className="font-medium text-purple-900">{asset.next_service_due_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                    </div>
                  )}
                  {asset.service_interval_hours && (
                    <div className="flex justify-between">
                      <span className="text-purple-700">Service Interval:</span>
                      <span className="font-medium text-purple-900">{asset.service_interval_hours}h</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments - Compact View for Excavators and Skid Steers */}
            {asset.asset_type === 'plant' && (asset.type === 'excavator' || asset.type === 'skid_steer') && asset.attachments && asset.attachments.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Paperclip className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-900">Attachments</span>
                  </div>
                  <span className="text-xs text-orange-700">{asset.attachments.length} total</span>
                </div>
                <div className="space-y-1">
                  {asset.attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-1 min-w-0 flex-1">
                        <span className="font-medium text-orange-900 truncate">{attachment.name}</span>
                      </div>
                      {attachment.location && (
                        <div className="flex items-center space-x-1 ml-2">
                          <MapPin className="h-3 w-3 text-orange-600 flex-shrink-0" />
                          <span className="text-orange-700 truncate max-w-[100px]">{attachment.location}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectedAsset(asset);
                  setShowServiceModal(true);
                }}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Service</span>
              </button>
              <button
                onClick={() => {
                  setSelectedAsset(asset);
                  setShowDetailModal(true);
                }}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAssets.length === 0 && (
        <div className="text-center py-12">
          <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterType || filterStatus ? 'No matching assets found' : 'No assets to service'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterType || filterStatus 
              ? 'Try adjusting your search or filter criteria.'
              : 'Add plant and machinery to track their service schedules alongside vehicles.'
            }
          </p>
          {(searchTerm || filterType || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Plant Machinery Form Modal */}
      {showPlantModal && (
        <PlantMachineryForm
          plant={editingPlant}
          onSubmit={handleSubmitPlant}
          onClose={() => {
            setShowPlantModal(false);
            setEditingPlant(null);
          }}
          loading={submitting}
        />
      )}

      {/* Service Record Form Modal */}
      {showServiceModal && selectedAsset && (
        <ServiceRecordForm
          asset={selectedAsset}
          onSubmit={handleSubmitService}
          onClose={() => {
            setShowServiceModal(false);
            setSelectedAsset(null);
          }}
          loading={submitting}
        />
      )}

      {/* Asset Detail Modal */}
      {showDetailModal && selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  selectedAsset.asset_type === 'vehicle' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {selectedAsset.asset_type === 'vehicle' ? (
                    <Truck className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Settings className="h-5 w-5 text-purple-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Service Details</h3>
                  <p className="text-sm text-gray-500">{selectedAsset.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAsset(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Asset Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Asset Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {selectedAsset.asset_type === 'vehicle' ? 'Registration' : 'Asset Number'}
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {selectedAsset.registration || selectedAsset.asset_number}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedAsset.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedAsset.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedAsset.status === 'inactive' ? 'bg-red-100 text-red-800' :
                      selectedAsset.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedAsset.status.charAt(0).toUpperCase() + selectedAsset.status.slice(1)}
                    </span>
                  </div>
                  {selectedAsset.make && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                      <p className="text-sm text-gray-900">{selectedAsset.make}</p>
                    </div>
                  )}
                  {selectedAsset.model && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <p className="text-sm text-gray-900">{selectedAsset.model}</p>
                    </div>
                  )}
                  {selectedAsset.year && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                      <p className="text-sm text-gray-900">{selectedAsset.year}</p>
                    </div>
                  )}
                  {selectedAsset.serial_number && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                      <p className="text-sm text-gray-900 font-mono">{selectedAsset.serial_number}</p>
                    </div>
                  )}
                  {selectedAsset.current_hours !== undefined && selectedAsset.current_hours !== null && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Hours</label>
                      <p className="text-sm text-gray-900">{selectedAsset.current_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hours</p>
                    </div>
                  )}
                  {selectedAsset.current_km !== undefined && selectedAsset.current_km !== null && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Kilometers</label>
                      <p className="text-sm text-gray-900">{Math.round(selectedAsset.current_km).toLocaleString()} km</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pin Dimensions - Excavators Only */}
              {selectedAsset.asset_type === 'plant' && selectedAsset.type === 'excavator' && (selectedAsset.pin_size || selectedAsset.between_ears || selectedAsset.pin_centres) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-amber-600" />
                    Pin Dimensions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {selectedAsset.pin_size && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pin Size</label>
                        <p className="text-sm text-gray-900 font-semibold">{selectedAsset.pin_size}</p>
                      </div>
                    )}
                    {selectedAsset.between_ears && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Between the Ears</label>
                        <p className="text-sm text-gray-900 font-semibold">{selectedAsset.between_ears}</p>
                      </div>
                    )}
                    {selectedAsset.pin_centres && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pin Centres</label>
                        <p className="text-sm text-gray-900 font-semibold">{selectedAsset.pin_centres}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Service History */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Service History</h4>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowServiceModal(true);
                    }}
                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Service</span>
                  </button>
                </div>
                
                {selectedAsset.service_records && selectedAsset.service_records.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAsset.service_records.map((service) => (
                      <div key={service.id} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Wrench className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {service.service_type} Service
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {formatDisplayDate(service.service_date)}
                            </span>
                            <button
                              onClick={() => handleDeleteService(service.id, selectedAsset.id, selectedAsset.asset_type)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                              title="Delete Service Record"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Description:</span>
                            <p className="text-gray-900">{service.description}</p>
                          </div>
                          {service.service_provider && (
                            <div>
                              <span className="font-medium text-gray-700">Provider:</span>
                              <p className="text-gray-900">{service.service_provider}</p>
                            </div>
                          )}
                          {service.cost !== undefined && service.cost !== null && (
                            <div>
                              <span className="font-medium text-gray-700">Cost:</span>
                              <p className="text-gray-900">${Number(service.cost).toFixed(2)}</p>
                            </div>
                          )}
                          {service.hours_at_service && (
                            <div>
                              <span className="font-medium text-gray-700">Hours at Service:</span>
                              <p className="text-gray-900">{service.hours_at_service} hours</p>
                            </div>
                          )}
                          {service.km_at_service && (
                            <div>
                              <span className="font-medium text-gray-700">Kilometers at Service:</span>
                              <p className="text-gray-900">{service.km_at_service.toLocaleString()} km</p>
                            </div>
                          )}
                        </div>
                        
                        {service.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="font-medium text-gray-700 text-sm">Notes:</span>
                            <p className="text-sm text-gray-900 mt-1">{service.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Wrench className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No service records found for this asset</p>
                  </div>
                )}
              </div>

              {/* Parts & Filter Numbers */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-gray-600" />
                    Parts & Filter Numbers
                  </h4>
                </div>

                {/* Add New Part */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Add Part / Filter</h5>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
                        <select
                          value={newPart.part_category}
                          onChange={(e) => setNewPart({ ...newPart, part_category: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          {PART_CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Part Name *</label>
                        <input
                          type="text"
                          value={newPart.part_name}
                          onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
                          placeholder="e.g., Oil Filter, Alternator"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Part Number *</label>
                        <input
                          type="text"
                          value={newPart.part_number}
                          onChange={(e) => setNewPart({ ...newPart, part_number: e.target.value })}
                          placeholder="e.g., WCO107, 26300-35504"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={newPart.notes}
                          onChange={(e) => setNewPart({ ...newPart, notes: e.target.value })}
                          placeholder="e.g., Supplier, cross-reference"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleAddPart}
                      className="flex items-center space-x-1 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Part</span>
                    </button>
                  </div>
                </div>

                {/* Parts List grouped by category */}
                {selectedAsset.parts && selectedAsset.parts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAsset.parts.map(part => (
                      <div key={part.id} className="bg-white rounded-lg border border-gray-200 p-3">
                        {editingPart?.id === part.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                                <select
                                  value={editingPart.part_category}
                                  onChange={(e) => setEditingPart({ ...editingPart, part_category: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                  {PART_CATEGORIES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Part Name</label>
                                <input
                                  type="text"
                                  value={editingPart.part_name}
                                  onChange={(e) => setEditingPart({ ...editingPart, part_name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Part Number</label>
                                <input
                                  type="text"
                                  value={editingPart.part_number}
                                  onChange={(e) => setEditingPart({ ...editingPart, part_number: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={editingPart.notes || ''}
                                  onChange={(e) => setEditingPart({ ...editingPart, notes: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleUpdatePart(editingPart)}
                                className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                              >
                                <Save className="h-3 w-3" />
                                <span>Save</span>
                              </button>
                              <button
                                onClick={() => setEditingPart(null)}
                                className="flex items-center space-x-1 px-3 py-1 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50 transition-colors"
                              >
                                <X className="h-3 w-3" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <span className={`mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getPartCategoryColor(part.part_category)}`}>
                                {getPartCategoryLabel(part.part_category)}
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900">{part.part_name}</p>
                                <p className="text-sm text-gray-600 font-mono">{part.part_number}</p>
                                {part.notes && <p className="text-xs text-gray-500 mt-0.5">{part.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                              <button
                                onClick={() => setEditingPart(part)}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePart(part.id)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Package className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No parts or filters saved yet</p>
                    <p className="text-xs text-gray-400 mt-1">Add part numbers above to keep track of filters and spare parts</p>
                  </div>
                )}
              </div>

              {/* Attachments - Only for Excavators and Skid Steers */}
              {selectedAsset.asset_type === 'plant' && (selectedAsset.type === 'excavator' || selectedAsset.type === 'skid_steer') && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900 flex items-center">
                      <Paperclip className="h-5 w-5 mr-2 text-gray-600" />
                      Attachments
                    </h4>
                  </div>

                  {/* Add New Attachment */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Add Attachment</h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Attachment Name *</label>
                        <input
                          type="text"
                          value={newAttachment.name}
                          onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                          placeholder="e.g., 12ft Bucket, Ripper"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Current Location</label>
                        <input
                          type="text"
                          value={newAttachment.location}
                          onChange={(e) => setNewAttachment({ ...newAttachment, location: e.target.value })}
                          placeholder="e.g., Site A, Workshop"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={newAttachment.notes}
                          onChange={(e) => setNewAttachment({ ...newAttachment, notes: e.target.value })}
                          placeholder="Additional notes"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleAddAttachment}
                        className="w-full flex items-center justify-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Attachment</span>
                      </button>
                    </div>
                  </div>

                  {/* Attachment List */}
                  {selectedAsset.attachments && selectedAsset.attachments.length > 0 ? (
                    <div className="space-y-3">
                      {selectedAsset.attachments.map((attachment) => (
                        <div key={attachment.id} className="bg-white rounded-lg border border-gray-200 p-4">
                          {editingAttachment?.id === attachment.id ? (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Attachment Name</label>
                                <input
                                  type="text"
                                  value={editingAttachment.name}
                                  onChange={(e) => setEditingAttachment({ ...editingAttachment, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Current Location</label>
                                <input
                                  type="text"
                                  value={editingAttachment.location}
                                  onChange={(e) => setEditingAttachment({ ...editingAttachment, location: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={editingAttachment.notes || ''}
                                  onChange={(e) => setEditingAttachment({ ...editingAttachment, notes: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUpdateAttachment(editingAttachment)}
                                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                                >
                                  <Save className="h-4 w-4" />
                                  <span>Save</span>
                                </button>
                                <button
                                  onClick={() => setEditingAttachment(null)}
                                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                                >
                                  <X className="h-4 w-4" />
                                  <span>Cancel</span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <Paperclip className="h-4 w-4 text-gray-600" />
                                  <span className="text-sm font-medium text-gray-900">{attachment.name}</span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => setEditingAttachment(attachment)}
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAttachment(attachment.id)}
                                    className="text-red-600 hover:text-red-800 transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              {attachment.location && (
                                <div className="flex items-start space-x-2 mb-2">
                                  <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs font-medium text-gray-500">Location:</span>
                                    <p className="text-sm text-gray-900">{attachment.location}</p>
                                  </div>
                                </div>
                              )}
                              {attachment.notes && (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <span className="text-xs font-medium text-gray-500">Notes:</span>
                                  <p className="text-sm text-gray-900 mt-1">{attachment.notes}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Paperclip className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No attachments added for this excavator</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Summary */}
      {assets.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Service Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {Math.round(((assets.length - overdueCount) / assets.length) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Service Compliance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {assets.filter(a => a.service_records && a.service_records.length > 0).length}
              </div>
              <div className="text-sm text-gray-600">Assets with Service History</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {plantAssets.length}
              </div>
              <div className="text-sm text-gray-600">Plant & Machinery</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {vehicleAssets.length}
              </div>
              <div className="text-sm text-gray-600">Vehicles & Trailers</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};